{
  "schema_version": 1,
  "task_id": "CAPABILITY-CAPSULE-001",
  "purpose": "Define AIOS Capability Capsules as auditable, default-off, evidence-bound units for internal and external capabilities.",
  "policy": {
    "formal_gates_not_replaced": true,
    "external_default_maturity_max": "C1",
    "default_off_required_for_external": true,
    "read_only_by_default_for_external": true,
    "no_secret_storage": true,
    "no_unknown_code_execution": true,
    "no_external_write_without_task_spec": true,
    "review_required_for_activation": true,
    "rollback_required_for_activation": true
  },
  "maturity_levels": [
    {
      "level": "C0",
      "name": "Reference",
      "description": "Read-only reference or design input. No execution authority.",
      "max_external_permissions": ["read_reference"]
    },
    {
      "level": "C1",
      "name": "Candidate",
      "description": "Source, license and risk are recorded. Still no default execution authority.",
      "max_external_permissions": ["read_reference", "static_review"]
    },
    {
      "level": "C2",
      "name": "Canary",
      "description": "May run smoke checks in an isolated environment; cannot replace formal Gate authority.",
      "max_external_permissions": ["read_reference", "static_review", "isolated_smoke"]
    },
    {
      "level": "C3",
      "name": "Active Read-only",
      "description": "May read scoped local or external status. Cannot write, deploy or send messages.",
      "max_external_permissions": ["read_reference", "static_review", "isolated_smoke", "scoped_read"]
    },
    {
      "level": "C4",
      "name": "Active Write-scoped",
      "description": "May write only inside a specific Task Spec, approval, Evidence and rollback envelope.",
      "max_external_permissions": ["task_scoped_write"]
    },
    {
      "level": "C5",
      "name": "Internalized Core",
      "description": "Critical capability internalized into the AIOS sovereign core and governed by formal Gates.",
      "max_external_permissions": ["sovereign_core"]
    }
  ],
  "required_controls": [
    "source_provenance",
    "license_notes",
    "security_notes",
    "permission_envelope",
    "adapter_seam",
    "smoke_contract",
    "evidence_contract",
    "rollback_contract",
    "reviewer_verdict"
  ],
  "capsules": [
    {
      "id": "aios-sovereign-kernel",
      "title_zh": "AIOS 主权最小内核",
      "title_en": "AIOS sovereign minimal kernel",
      "source": {
        "kind": "internal",
        "location": "crates/* + apps/control-console",
        "provenance": "L0 Evidence and project Git history",
        "license": "project-owned"
      },
      "maturity": "C5",
      "category": "core",
      "status": "active",
      "permission_envelope": {
        "default_enabled": true,
        "read_only_by_default": false,
        "external_write_by_default": false,
        "can_execute": true,
        "can_write": true,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "AIOS local daemon and typed command/event boundaries",
      "smoke_contract": "L0 regression and component tests",
      "evidence_contract": "artifacts/evidence/* L0 tasks",
      "rollback_contract": "Git revert or task-specific rollback notes",
      "reviewer_verdict": "approved",
      "next_action_zh": "保持主权边界，不把状态交给任何外部 Agent 或插件。",
      "next_action_en": "Keep sovereign boundaries; never hand durable state to external agents or plugins."
    },
    {
      "id": "aios-context-pack",
      "title_zh": "Context Pack",
      "title_en": "Context Pack",
      "source": {
        "kind": "internal",
        "location": "scripts/build-context-pack.mjs",
        "provenance": "L2-CONTEXT-001 Evidence",
        "license": "project-owned"
      },
      "maturity": "C5",
      "category": "devx",
      "status": "active",
      "permission_envelope": {
        "default_enabled": true,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": true,
        "can_write": true,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "task-scoped manifest builder",
      "smoke_contract": "node --test scripts/build-context-pack.test.mjs",
      "evidence_contract": "artifacts/evidence/L2-CONTEXT-001",
      "rollback_contract": "revert context-pack config/script changes",
      "reviewer_verdict": "approved",
      "next_action_zh": "作为新会话和 Reviewer 的上下文压缩器，但不替代全项目通读。",
      "next_action_en": "Use as context compression for new sessions and reviewers, never as a replacement for project reading."
    },
    {
      "id": "official-site-living-blueprint",
      "title_zh": "官网 Living Blueprint",
      "title_en": "Official site Living Blueprint",
      "source": {
        "kind": "internal",
        "location": "apps/aios-official-site",
        "provenance": "SITE-001 and SITE-V13-LIVING-BLUEPRINT-001",
        "license": "project-owned"
      },
      "maturity": "C3",
      "category": "ui",
      "status": "active-readonly",
      "permission_envelope": {
        "default_enabled": true,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": true,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "Cloudflare Pages Functions status/OAuth boundary",
      "smoke_contract": "pnpm site:aios:check && pnpm --filter @aios/official-site test",
      "evidence_contract": "artifacts/evidence/SITE-001",
      "rollback_contract": "Cloudflare Pages previous deployment + Git revert",
      "reviewer_verdict": "approved-for-SITE-001; v1.3 upgrade pending review",
      "next_action_zh": "展示 v1.3、τ、胶囊、进度和反黑洞 Gate，只读不扩权。",
      "next_action_en": "Show v1.3, tau, capsules, progress and anti-black-hole gates without expanding authority."
    },
    {
      "id": "gui-os-cockpit",
      "title_zh": "GUI OS Cockpit",
      "title_en": "GUI OS Cockpit",
      "source": {
        "kind": "internal",
        "location": "apps/control-console",
        "provenance": "UI-001 and GUI-V13-COCKPIT-001",
        "license": "project-owned"
      },
      "maturity": "C3",
      "category": "ui",
      "status": "active-readonly",
      "permission_envelope": {
        "default_enabled": true,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": true,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "Tauri IPC / demo transport / local daemon command envelope",
      "smoke_contract": "pnpm --filter @aios/control-console test:unit",
      "evidence_contract": "artifacts/evidence/UI-001, artifacts/evidence/DESKTOP-RUNTIME-GOVERNED-ACTION-PREVIEW-001, artifacts/evidence/DESKTOP-RUNTIME-PREVIEW-ACTION-QUEUE-001, artifacts/evidence/DESKTOP-RUNTIME-APPROVAL-DECISION-SIM-001 and artifacts/evidence/DESKTOP-RUNTIME-APPROVAL-RECEIPT-DRAFT-001",
      "rollback_contract": "Git revert; no domain state migration",
      "reviewer_verdict": "approved-for-UI-001; DESKTOP-RUNTIME-GOVERNED-ACTION-PREVIEW-001, DESKTOP-RUNTIME-PREVIEW-ACTION-QUEUE-001, DESKTOP-RUNTIME-APPROVAL-DECISION-SIM-001 and DESKTOP-RUNTIME-APPROVAL-RECEIPT-DRAFT-001 reviewed with fact-only caveats; UI remains active-readonly",
      "next_action_zh": "继续把桌面 Runtime 动作做成可解释、可审批、可回滚的 preview-only 前台；不直接执行。",
      "next_action_en": "Continue turning Desktop Runtime actions into explainable, approvable and rollback-aware preview-only surfaces; do not execute directly."
    },
    {
      "id": "cloudflare-wrangler",
      "title_zh": "Cloudflare Wrangler",
      "title_en": "Cloudflare Wrangler",
      "source": {
        "kind": "local-skill-cli",
        "location": "wrangler project-local dependency",
        "provenance": "SITE-001 deployment evidence and Cloudflare docs",
        "license": "third-party; npm package license applies"
      },
      "maturity": "C1",
      "category": "cloud",
      "status": "candidate",
      "permission_envelope": {
        "default_enabled": false,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": false,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "task-scoped Cloudflare deployment script",
      "smoke_contract": "wrangler whoami only when deployment task is active",
      "evidence_contract": "deployment note with no secret echo",
      "rollback_contract": "Cloudflare Pages previous deployment",
      "reviewer_verdict": "candidate",
      "next_action_zh": "继续作为任务内工具，不成为 AIOS 默认 Runtime 依赖。",
      "next_action_en": "Remain a task-scoped tool, not a default AIOS runtime dependency."
    },
    {
      "id": "github-source-intake",
      "title_zh": "GitHub 源只读准入",
      "title_en": "GitHub source read-only intake",
      "source": {
        "kind": "external-source",
        "location": "GitHub repository URLs supplied by user or search",
        "provenance": "GITHUB-SOURCE-INTAKE-001 Evidence",
        "license": "unknown until repository-specific audit"
      },
      "maturity": "C0",
      "category": "external-source",
      "status": "reference",
      "permission_envelope": {
        "default_enabled": false,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": false,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "config/github-source-intake.json source-intake manifest",
      "smoke_contract": "pnpm devx:github-source-intake:check",
      "evidence_contract": "artifacts/evidence/GITHUB-SOURCE-INTAKE-001",
      "rollback_contract": "delete candidate manifest; no executed code",
      "reviewer_verdict": "approved for BEA-LOCAL-MODEL-ROUTER-DRY-RUN-001 proposal-only boundary; no fact/execution/Runtime authority",
      "next_action_zh": "从候选 GitHub 源中选择一个进入 Task Spec；只读审计 README/license/metadata，不执行未知代码。",
      "next_action_en": "Select one candidate GitHub source for a Task Spec; read README/license/metadata only and do not execute unknown code."
    },
    {
      "id": "github-plugin-candidate",
      "title_zh": "GitHub 插件候选",
      "title_en": "GitHub plugin candidate",
      "source": {
        "kind": "recommended-plugin",
        "location": "github@openai-curated-remote",
        "provenance": "recommended plugin catalog",
        "license": "provider/plugin terms apply"
      },
      "maturity": "C0",
      "category": "plugin",
      "status": "available-not-installed",
      "permission_envelope": {
        "default_enabled": false,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": false,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "future explicit plugin installation task",
      "smoke_contract": "none until installed by explicit user action",
      "evidence_contract": "future plugin install/connect Evidence",
      "rollback_contract": "plugin uninstall/disconnect if installed later",
      "reviewer_verdict": "not-installed",
      "next_action_zh": "仅登记为候选；安装和授权必须另行明确。",
      "next_action_en": "Catalog only; installation and authorization require a separate explicit task."
    },
    {
      "id": "figma-plugin-candidate",
      "title_zh": "Figma 插件候选",
      "title_en": "Figma plugin candidate",
      "source": {
        "kind": "recommended-plugin",
        "location": "figma@openai-curated-remote",
        "provenance": "recommended plugin catalog",
        "license": "provider/plugin terms apply"
      },
      "maturity": "C0",
      "category": "plugin",
      "status": "available-not-installed",
      "permission_envelope": {
        "default_enabled": false,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": false,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "future explicit design handoff task",
      "smoke_contract": "none until installed by explicit user action",
      "evidence_contract": "future design/Evidence handoff",
      "rollback_contract": "plugin uninstall/disconnect if installed later",
      "reviewer_verdict": "not-installed",
      "next_action_zh": "仅登记为 UI/UX 候选能力，不写设计文件。",
      "next_action_en": "Catalog only as a UI/UX candidate; do not write design files."
    },
    {
      "id": "llm-model-routing-candidate",
      "title_zh": "LLM / 模型路由候选",
      "title_en": "LLM / model routing candidate",
      "source": {
        "kind": "external-service",
        "location": "OpenAI-compatible, local and open-source model providers",
        "provenance": "future L2-CAPABILITY-INTAKE-UI-001 and model-routing Task Spec",
        "license": "provider/model-specific terms apply"
      },
      "maturity": "C0",
      "category": "model",
      "status": "reference",
      "permission_envelope": {
        "default_enabled": false,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": false,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "future model provider manifest and router policy",
      "smoke_contract": "static provider capability and policy review only",
      "evidence_contract": "future model-routing Evidence",
      "rollback_contract": "delete provider candidate manifest; no credential retained",
      "reviewer_verdict": "not-reviewed",
      "next_action_zh": "先登记模型能力、成本、权限和凭据边界；不默认调用付费 API。",
      "next_action_en": "Catalog model capability, cost, permission and credential boundaries first; do not call paid APIs by default."
    },
    {
      "id": "codex-engine-reference",
      "title_zh": "Codex 工程执行引擎参考",
      "title_en": "Codex engineering engine reference",
      "source": {
        "kind": "external-agent",
        "location": "Codex session/workspace execution surface",
        "provenance": "project collaboration history and current Codex execution envelope",
        "license": "platform terms apply"
      },
      "maturity": "C0",
      "category": "agent",
      "status": "reference",
      "permission_envelope": {
        "default_enabled": false,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": false,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "future explicit Codex execution capsule boundary",
      "smoke_contract": "read-only task/Evidence review until explicit execution task",
      "evidence_contract": "future Codex-engine Evidence",
      "rollback_contract": "remove execution capsule manifest; retain project Git rollback",
      "reviewer_verdict": "not-reviewed",
      "next_action_zh": "Codex 作为工程执行专家被 AIOS 调度，不等同于 AIOS 本体。",
      "next_action_en": "Codex is orchestrated by AIOS as an engineering expert; it is not the AIOS body."
    },
    {
      "id": "project-skill-catalog",
      "title_zh": "项目 Skill 目录",
      "title_en": "Project Skill catalog",
      "source": {
        "kind": "internal",
        "location": "skills/* + config/project-skills.json",
        "provenance": "DEVX-SKILL-001",
        "license": "project-owned"
      },
      "maturity": "C3",
      "category": "skill",
      "status": "active-readonly",
      "permission_envelope": {
        "default_enabled": true,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": true,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "project-skills registry and skill read-before-use protocol",
      "smoke_contract": "pnpm devx:skills:check",
      "evidence_contract": "artifacts/evidence/DEVX-SKILL-001 and artifacts/evidence/L5-SKILL-CAPSULE-PATTERN-001",
      "rollback_contract": "revert skill registry or specific skill directory",
      "reviewer_verdict": "approved; L5-SKILL-CAPSULE-PATTERN-001 reviewed pass",
      "next_action_zh": "把成功流程继续沉淀为项目 Skill，并由 L5 drift monitor 检查 Skill、胶囊、GUI、官网和观察态是否漂移；不能自授权或替代 Gate。",
      "next_action_en": "Continue promoting successful flows into project Skills and use the L5 drift monitor to check Skill, Capsule, GUI, site and observational-status drift; never self-authorize or replace Gates."
    },
    {
      "id": "browser-automation-candidate",
      "title_zh": "Browser 观察与自动化候选",
      "title_en": "Browser observation and automation candidate",
      "source": {
        "kind": "local-skill",
        "location": "browser/chrome/playwright skills",
        "provenance": "DEVX-MCP-CATALOG-001 and local UI smoke evidence",
        "license": "tool/provider-specific terms apply"
      },
      "maturity": "C1",
      "category": "browser",
      "status": "candidate",
      "permission_envelope": {
        "default_enabled": false,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": false,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "future browser observation manifest and action allowlist",
      "smoke_contract": "local screenshot/geometry smoke only unless a Task Spec expands scope",
      "evidence_contract": "future browser-capsule Evidence",
      "rollback_contract": "delete candidate manifest; no browser profile mutation",
      "reviewer_verdict": "candidate",
      "next_action_zh": "先用于本地 UI 观察和截图证据；账号操作、提交和外部写入必须另行授权。",
      "next_action_en": "Use first for local UI observation and screenshot evidence; account actions, submissions and external writes need separate authorization."
    },
    {
      "id": "vscode-ide-surface",
      "title_zh": "VS Code / IDE 工作面",
      "title_en": "VS Code / IDE work surface",
      "source": {
        "kind": "internal",
        "location": "extensions/vscode-aios",
        "provenance": "L2-VSCODE-001",
        "license": "project-owned"
      },
      "maturity": "C3",
      "category": "ide",
      "status": "active-readonly",
      "permission_envelope": {
        "default_enabled": true,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": true,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "VS Code extension model/tree/command boundary",
      "smoke_contract": "pnpm devx:vscode:check",
      "evidence_contract": "artifacts/evidence/L2-VSCODE-001",
      "rollback_contract": "revert extension files and package entries",
      "reviewer_verdict": "approved",
      "next_action_zh": "作为 IDE 入口展示 AIOS 状态和受控动作，不绕过本地 Runtime。",
      "next_action_en": "Expose AIOS status and governed actions as an IDE surface without bypassing the local Runtime."
    },
    {
      "id": "expert-agent-federation-candidate",
      "title_zh": "专家 Agent 联邦候选",
      "title_en": "Expert agent federation candidate",
      "source": {
        "kind": "external-agent",
        "location": "Grok / Kimi / DeepSeek / domain agents",
        "provenance": "future Runtime Driver Federation Task Specs",
        "license": "provider/model-specific terms apply"
      },
      "maturity": "C0",
      "category": "agent",
      "status": "reference",
      "permission_envelope": {
        "default_enabled": false,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": false,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "future runtime-driver adapter and arbitration policy",
      "smoke_contract": "static capability and prompt-boundary review only",
      "evidence_contract": "future Runtime Driver Federation Evidence",
      "rollback_contract": "remove candidate adapter and route policy",
      "reviewer_verdict": "not-reviewed",
      "next_action_zh": "只登记为专家能力池候选；正式接入前必须完成 Adapter、Evidence 和 Reviewer。",
      "next_action_en": "Catalog only as an expert pool candidate; Adapter, Evidence and Reviewer are required before activation."
    },
    {
      "id": "local-openai-compatible-adapter",
      "title_zh": "本地 OpenAI-compatible 适配层",
      "title_en": "Local OpenAI-compatible adapter layer",
      "source": {
        "kind": "internal",
        "location": "config/local-model-provider-choice.json",
        "provenance": "BC1-LOCAL-MODEL-PROVIDER-CHOICE-001",
        "license": "project-owned"
      },
      "maturity": "C1",
      "category": "model-adapter",
      "status": "selected-default-off",
      "permission_envelope": {
        "default_enabled": false,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": false,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "OpenAI-compatible localhost HTTP contract exposed through AIOS model-provider policy",
      "smoke_contract": "pnpm devx:local-model-provider-choice:check; no model call and no external network",
      "evidence_contract": "artifacts/evidence/BC1-LOCAL-MODEL-PROVIDER-CHOICE-001",
      "rollback_contract": "revert config/local-model-provider-choice.json and associated Evidence/status entries",
      "reviewer_verdict": "pending review",
      "next_action_zh": "作为 AIOS 模型层统一抽象；真实 provider 可替换，当前默认关闭且不能调用模型。",
      "next_action_en": "Act as the unified AIOS model-layer abstraction; real providers remain replaceable, default-off and unable to call models now."
    },
    {
      "id": "ollama-local-provider-capsule",
      "title_zh": "Ollama 本地 Provider 胶囊",
      "title_en": "Ollama local provider capsule",
      "source": {
        "kind": "external-service",
        "location": "http://127.0.0.1:11434",
        "provenance": "BC1-LOCAL-MODEL-PROVIDER-CHOICE-001 and official Ollama docs",
        "license": "Ollama/provider/model-specific terms apply"
      },
      "maturity": "C1",
      "category": "model-provider",
      "status": "governed-action-preview-visible-no-authority",
      "permission_envelope": {
        "default_enabled": false,
        "read_only_by_default": true,
        "external_write_by_default": false,
        "can_execute": false,
        "can_write": false,
        "can_deploy": false,
        "can_send_messages": false,
        "can_store_secrets": false,
        "requires_task_spec_for_write": true,
        "requires_user_approval_for_high_risk": true
      },
      "adapter_seam": "Ollama native API and OpenAI-compatible localhost endpoints behind the AIOS adapter",
      "smoke_contract": "BC1-QWEN25-CODER-TINY-PROMPT-SMOKE-001 proves one bounded local generation; BEA-LOCAL-MODEL-ROUTER-DRY-RUN-001 admits one output only as advisory-only proposal; BEA-LOCAL-MODEL-ROUTER-POLICY-SIM-001 proves fenced/malformed/command/Runtime/tool/file/fact-claim policy behavior with zero model calls; BEA-LOCAL-MODEL-ROUTER-UI-PREVIEW-001 proves these states are visible in AIOS Home with no model-output execution button; BEA-LOCAL-MODEL-LIVE-PROPOSAL-PREVIEW-001 proves one explicit bounded live proposal can pass policy as advisory-only; BEA-LOCAL-MODEL-LIVE-PROPOSAL-UI-001 proves the receipt is visible in AIOS Home with no fact/execution/Runtime authority; BEA-LOCAL-MODEL-ADVISORY-FEEDBACK-LOOP-001 proves the live suggestion can feed Task Spec draft context with user feedback while still not executing; BEA-LOCAL-MODEL-ADVISORY-DRAFT-EXPORT-001 proves the fused draft is manually copyable without Clipboard API, formal task writes or Runtime authority; DESKTOP-RUNTIME-GOVERNED-ACTION-PREVIEW-001 proves the fused candidate can enter a preview-only approval/permission-ledger card with no Tauri command, execution button, formal task write, model call or Runtime authority",
      "evidence_contract": "artifacts/evidence/BC1-LOCAL-MODEL-PROVIDER-CHOICE-001, artifacts/evidence/BC1-OLLAMA-LOCALHOST-READINESS-PROBE-001, artifacts/evidence/BC1-OLLAMA-LOCAL-SETUP-REQUIRED-001, artifacts/evidence/BEA-LOCAL-MODEL-ADAPTER-SMOKE-001, artifacts/evidence/BC1-OLLAMA-MODEL-SELECTION-001, artifacts/evidence/BC1-OLLAMA-MODEL-DOWNLOAD-QWEN25-CODER-7B-001, artifacts/evidence/BC1-QWEN25-CODER-TINY-PROMPT-SMOKE-001, artifacts/evidence/BEA-LOCAL-MODEL-ROUTER-DRY-RUN-001, artifacts/evidence/BEA-LOCAL-MODEL-ROUTER-POLICY-SIM-001, artifacts/evidence/BEA-LOCAL-MODEL-ROUTER-UI-PREVIEW-001, artifacts/evidence/BEA-LOCAL-MODEL-LIVE-PROPOSAL-PREVIEW-001, artifacts/evidence/BEA-LOCAL-MODEL-LIVE-PROPOSAL-UI-001, artifacts/evidence/BEA-LOCAL-MODEL-ADVISORY-FEEDBACK-LOOP-001, artifacts/evidence/BEA-LOCAL-MODEL-ADVISORY-DRAFT-EXPORT-001 and artifacts/evidence/DESKTOP-RUNTIME-GOVERNED-ACTION-PREVIEW-001",
      "rollback_contract": "stop recorded Ollama PID if rollback is requested; remove only exact qwen2.5-coder:7b manifest/blob hashes from the project-isolated model store; do not remove user-supplied manual download or unrelated models",
      "reviewer_verdict": "approved for DESKTOP-RUNTIME-GOVERNED-ACTION-PREVIEW-001 preview-only governed-action card; caveat: replacement reviewer was fact-only and did not inspect files; no Tauri/formal-task/model/runtime/tool/external effects",
      "next_action_zh": "qwen2.5-coder:7b 的 live proposal 已进入桌面 Runtime 受控动作预览卡；下一步做 preview-only 待审批队列，仍不执行。",
      "next_action_en": "The qwen2.5-coder:7b live proposal now feeds the Desktop Runtime governed-action preview card; next build a preview-only pending-approval queue while still not executing."
    }
  ]
}
